/*
 * A conversation's own menu (X-A4 D4, N3) — the five columns of `chat_conversation_member` a person
 * may set for themselves, in the two places they are about: the sidebar row, and the band above the
 * conversation itself (VISUAL.md §7.3).
 *
 * In the sidebar the trigger is a `⋯` that arrives with the pointer and while anything in the row has
 * focus, and it is deliberately **not** a tab stop: the sidebar is a list to walk, and a second stop
 * per row would double the distance from the rail to the conversation. The keyboard route is the row
 * itself — `Shift+F10` and the context-menu key, the two gestures a platform menu has always answered
 * (`opensMenu`) — and the pointer route is the button or the right mouse button, which is where
 * everybody tries first. That is why the row hands the open state in and says where the focus goes at
 * close. In the band there is no row behind the button: the menu is a tab stop of its own, holds its
 * own state, and the primitive returns the focus to the trigger by itself.
 *
 * Almost every entry is one `PATCH …/self` through `api.ts` and nothing else: the answer is ignored
 * and the row redraws when `chat.membership.self` arrives, so the menu cannot disagree with the server
 * even for a frame. Mute and the notification level are two independent axes — the mute is a moment,
 * the level is a standing choice — and each says only what it is. The three that are not a membership
 * column are *Copy link*, which touches nothing, *Mark as read*, which moves the read cursor over the
 * socket the way `Shift+Escape` does, and a direct conversation's switch for AI chats, which is the
 * conversation's own and not the reader's (`conversation/agents.tsx`): it redraws when
 * `chat.conversation.updated` arrives, the same way.
 *
 * An entry whose whole effect is somewhere the reader is not looking says so in a toast, with the
 * words the announcer already says (VISUAL.md §6.10): copying a link changes nothing on the
 * screen at all, and muting changes a row that may be three sections away.
 */

import { useState, type KeyboardEvent } from "react"
import { t as coreT } from "../../../core/i18n/index.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { Icon, type IconName } from "../../../core/shell/icons.tsx"
import { useAnnounce, useToast } from "../../../core/shell/index.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, FormAlert, Menu, type MenuItem } from "../../../ui/index.ts"
import { AgentsConfirm, maySwitchAgents } from "../conversation/agents.tsx"
import { leaveConversation } from "../conversation/api.ts"
import { setAgents } from "../conversation/manage.ts"
import { goTo } from "../conversation/opening.ts"
import { rejoinableAfterLeaving } from "../conversation/owners.ts"
import { chatDeps } from "../deps.tsx"
import { t } from "../i18n/t.ts"
import { isDirect, isMuted } from "../state/selectors.ts"
import { NotifyAll, NotifyMention, NotifyNone, type MemberSelf } from "../wire/chat.gen.ts"
import type { ConversationView } from "../wire/views.ts"
import { MUTED_UNTIL_FURTHER_NOTICE, updateSelf } from "./api.ts"

/** Where leaving lands, because the conversation has just stopped being a place. */
const AFTER_LEAVING = "/"

/**
 * The three notification levels as the menu offers them: the wire value, its copy and nothing else,
 * so a fourth level the server learns is one row in this list.
 */
const LEVELS = [
	{ value: NotifyAll, key: "chat.menu.levelAll" },
	{ value: NotifyMention, key: "chat.menu.levelMention" },
	{ value: NotifyNone, key: "chat.menu.levelNone" },
] as const

/**
 * The two keyboard gestures that open a context menu. They are the row's to answer, because the row
 * is what has focus; the button is only the pointer's way in.
 */
export function opensMenu(event: KeyboardEvent): boolean {
	return event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey)
}

interface ConversationMenuProps {
	conv: ConversationView
	/** This reader's own membership row — the whole state the menu reads and writes. */
	self: MemberSelf
	/** How the surface names the place, so the trigger says which conversation it is the menu of. */
	label: string
	/** The conversation's own address; *Copy link* copies it with the origin in front. */
	path: string
	/** The trigger's class: the sidebar row's hover-revealed `⋯`, or the band's own glyph button. */
	className?: string
	/**
	 * Whether the menu is open. A sidebar row holds it, because the keyboard route is the row's: a
	 * browser reports `Shift+F10` as a key on the focused link, and a button that is only *clicked*
	 * would never hear it — Radix opens on a pointer press, not on a synthetic click. Left out, the
	 * menu holds its own state, which is what the band needs.
	 */
	open?: boolean
	onOpenChange?: (open: boolean) => void
	/**
	 * Where the focus belongs when the menu closes. A row hands its own link, because the `⋯` is no tab
	 * stop there and is not even drawn once the pointer and the focus have left the row: the
	 * primitive's own "put it back on the trigger" would reach for a `display: none` element and leave
	 * the focus on `<body>`, and the reader's next Tab would start again at the top of the document.
	 * Left out — the band, where the trigger is a tab stop that is always drawn — the primitive is
	 * right and does it itself.
	 */
	focusRow?: () => void
	/**
	 * Offered as the menu's first entry where there is a details column to open: the band's `⋯` is the
	 * one control that survives below 768 px, so the way to About, Files and Pinned has to be in it
	 * (D-10 D9). A sidebar row passes none — a row is not the conversation one is reading.
	 */
	onOpenDetails?: () => void
}

export function ConversationMenu({
	conv,
	self,
	label,
	path,
	className = "tb-conv-row__menu",
	open,
	onOpenChange,
	focusRow,
	onOpenDetails,
}: ConversationMenuProps) {
	const announce = useAnnounce()
	const toast = useToast()
	const deps = chatDeps()
	const [leaving, setLeaving] = useState(false)
	const [openingToAgents, setOpeningToAgents] = useState(false)
	const reader = useModel((model) => model.me)
	// The band's own menu, for which no surface above holds the flag.
	const [openHere, setOpenHere] = useState(false)
	const isOpen = open ?? openHere
	const setOpen = onOpenChange ?? setOpenHere

	/**
	 * One write, one place a refusal is reported from. A menu has no room for a sentence and has
	 * closed by the time the answer arrives, so the failure is a toast, which stays until it is
	 * dismissed. What succeeded is reported by whichever entry asked for it: a star redraws the row
	 * and needs no words, a mute may change a row the reader cannot see.
	 */
	const write = (patch: Parameters<typeof updateSelf>[1], said?: () => void) => {
		void updateSelf(conv.id, patch).then(
			() => said?.(),
			() => toast({ kind: "error", key: "chat.menu.failed" }),
		)
	}

	const copyLink = () => {
		void navigator.clipboard
			?.writeText(`${globalThis.location.origin}${path}`)
			.then(() => {
				announce("chat.message.copied")
				toast({ kind: "success", key: "chat.message.copied" })
			})
			.catch(() => toast({ kind: "error", key: "chat.menu.failed" }))
	}

	// The clock ticks on the minute, like every other time on the screen (`useNow`), so a mute that has
	// run out stops calling itself one. The product sets no mute with an end today — the entry is
	// *Mute* and *Unmute*, not a duration — so this is the honest reading of a column that may one day
	// carry one.
	const muted = isMuted(self, useNow())
	const direct = isDirect(conv.kind)
	const items: MenuItem[] = []
	if (onOpenDetails !== undefined) {
		items.push({
			id: "details",
			label: t("chat.header.detailsTitle"),
			icon: "info",
			onSelect: onOpenDetails,
		})
	}
	items.push(
		{
			id: "star",
			label: self.starred ? t("chat.menu.unstar") : t("chat.menu.star"),
			icon: self.starred ? "starFilled" : "star",
			separatorBefore: onOpenDetails !== undefined,
			onSelect: () => write({ starred: !self.starred }),
		},
		{
			id: "mute",
			label: muted ? t("chat.menu.unmute") : t("chat.menu.mute"),
			icon: muted ? "bellOff" : "bell",
			onSelect: () =>
				write({ mutedUntil: muted ? 0 : MUTED_UNTIL_FURTHER_NOTICE }, () =>
					toast({
						kind: "success",
						key: muted ? "chat.menu.unmuted" : "chat.menu.muted",
						args: { name: label },
					}),
				),
		},
		...LEVELS.map((level, index) => ({
			id: `level.${level.value}`,
			label: t(level.key),
			radioGroup: "level",
			groupLabel: t("chat.menu.level"),
			selected: self.notifyLevel === level.value,
			separatorBefore: index === 0,
			onSelect: () => write({ notifyLevel: level.value }),
		})),
	)
	// Hiding is a direct conversation's own move: a channel one no longer wants is left, and a row
	// that could be hidden without leaving would be a membership nobody could see any more.
	if (direct) {
		items.push({
			id: "hide",
			label: t("chat.menu.hide"),
			icon: "eyeOff",
			separatorBefore: true,
			onSelect: () => write({ hidden: true }),
		})
	}
	/*
	 * A direct or group conversation's switch for AI chats (S-19 §6, A-4 §3.2): its details column is
	 * the other person's profile, so the switch stands with its other own facts, here. A tick while it
	 * is on; choosing it while off asks first (`AgentsConfirm`), while on closes it at once — the
	 * server writes the line both sides read either way. A guest participant is offered nothing.
	 */
	if (direct && maySwitchAgents(conv, self.state === "active", reader)) {
		items.push({
			id: "agents",
			label: t("chat.menu.agents"),
			icon: "aichat",
			checked: conv.agentsAvailable,
			separatorBefore: true,
			onSelect: () => {
				if (!conv.agentsAvailable) {
					setOpeningToAgents(true)
					return
				}
				void setAgents(conv.id, false).catch(() =>
					toast({ kind: "error", key: "chat.menu.failed" }),
				)
			},
		})
	}
	/*
	 * Marking a conversation read from its row: the sidebar gesture a daily reader reaches for most,
	 * and until now only `Shift+Escape` inside the conversation itself (X-B1 A6). It is that shortcut's
	 * own write — the deliberate one, `auto: false`, which is what lifts a hold somebody deliberately
	 * put on a conversation (`chat/unread/cursor.go`) — and it says that shortcut's own sentence,
	 * because a row three sections up the column changing quietly is a change nobody sees.
	 *
	 * A conversation with nothing in it has no cursor to move, and an instance whose chat module bound
	 * no `markRead` has nowhere to move it: neither offers the entry rather than offering a refusal.
	 */
	if (deps.markRead !== undefined && conv.lastSeq > 0) {
		items.push({
			id: "markRead",
			label: t("chat.menu.markRead"),
			icon: "check",
			separatorBefore: true,
			onSelect: () => {
				void deps
					.markRead?.({ conv: conv.id, seq: conv.lastSeq, auto: false })
					?.catch(() => undefined)
				toast({ kind: "success", key: "chat.read.marked", args: { conversation: label } })
			},
		})
	}
	// A copied address is the action with no visible effect at all, so it is the one that always says
	// something. The glyph is `copy`, because `link` means a hyperlink (D-04 D4-15).
	items.push({
		id: "copyLink",
		label: t("chat.menu.copyLink"),
		icon: "copy",
		separatorBefore: !direct,
		onSelect: copyLink,
	})
	if (!direct) {
		items.push({
			id: "leave",
			label: t("chat.members.leave"),
			icon: "logOut",
			danger: true,
			separatorBefore: true,
			onSelect: () => setLeaving(true),
		})
	}

	return (
		<>
			<Menu
				open={isOpen}
				onOpenChange={setOpen}
				// A row is where the reader was and where the next `Shift+F10` has to arrive, so the menu
				// hands the focus back there itself instead of letting the primitive aim at a trigger that
				// is no longer drawn. The band's trigger is always drawn, and the primitive is then right.
				{...(focusRow === undefined
					? {}
					: {
							onCloseAutoFocus: (event: Event) => {
								event.preventDefault()
								focusRow()
							},
						})}
				trigger={
					<Button
						variant="ghost"
						quiet
						icon
						size="sm"
						className={className}
						{...(focusRow === undefined ? {} : { tabIndex: -1 })}
						aria-label={t("chat.menu.open", { name: label })}
						before={<Icon name={"more" satisfies IconName} size={16} />}
						// The pointer's door, and it answers the two keyboard gestures as well while it has
						// the focus. In the sidebar, closing does not put the focus back here: the button is
						// drawn only while the row is hovered, focused or open, so the focus goes to the row
						// (`onCloseAutoFocus` above), which is the one stop that list has per conversation.
						onKeyDown={(event) => {
							if (!opensMenu(event)) return
							event.preventDefault()
							setOpen(true)
						}}
					/>
				}
				items={items}
			/>
			<LeaveDialog conv={conv} path={path} open={leaving} onOpenChange={setLeaving} />
			{direct ? (
				<AgentsConfirm meta={conv} open={openingToAgents} onOpenChange={setOpeningToAgents} />
			) : null}
		</>
	)
}

/**
 * Leaving from the row. The same confirmation stands in the details pane, whose copy this shares;
 * what differs is where it leaves from — a reader who leaves the conversation they are reading has
 * to be taken somewhere, and one who leaves another row stays where they are.
 */
function LeaveDialog({
	conv,
	path,
	open,
	onOpenChange,
}: {
	conv: ConversationView
	path: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const leave = useAsyncAction(async () => {
		await leaveConversation(conv.id)
		onOpenChange(false)
		if (globalThis.location.pathname === path) goTo(AFTER_LEAVING)
	})
	const rejoinable = useModel((model) => rejoinableAfterLeaving(conv, model.groups, model.me))

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("chat.members.leaveTitle")}
			description={rejoinable ? t("chat.members.leaveBody") : t("chat.members.leavePrivateBody")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					<Button
						variant="danger"
						label={t("chat.members.leave")}
						disabled={leave.busy}
						onClick={() => void leave.run()}
					/>
				</div>
			}
		>
			<FormAlert messageKey={leave.error} />
		</Dialog>
	)
}
